import React, { ButtonHTMLAttributes } from 'react';
import cn from 'classnames';
import IntegrationIcons from '@components/Icons/IntegrationIcons';
import styles from './Buttons.module.scss';
import StateBasedFadeTransition from '@components/transitions/StateBasedFadeTransition';
import { LoaderIcon } from '@components/Icons';

interface CustomButtonProps {
  onClick: React.MouseEventHandler;
  inProgress: boolean;
  isSignUp?: boolean;
  disabled?: boolean;
  className?: string;
  showLastUsedTag?: boolean;
}

export function ToggleButton({
  className,
  onClick,
  disabled,
  children,
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(styles.toggleBtn, className)}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

export const LastUsedTag = () => (
  <div className={styles.lastLoginMethodTag}>
    <svg width="6" height="15" viewBox="0 0 6 15">
      <path
        strokeWidth="0.5"
        d="M5 14.8c0-1.52-1.06-3.3-3.13-5.36a2.87 2.87 0 0 1-.92-1.75 2.1 2.1 0 0 1 .6-1.71c2.3-2.2 3.45-4.08 3.45-5.68v-1h1v16h-1Z"
      />
    </svg>
    <div className={styles.bg}>Last used</div>
  </div>
);

export function LoginButton({
  children,
  onClick,
  disabled,
  className,
  showLastUsedTag,
}: { showLastUsedTag?: boolean } & ButtonHTMLAttributes<{}>) {
  return (
    <div className={styles.loginBtnContainer}>
      <button
        type="button"
        className={cn(styles.button, className)}
        onClick={onClick}
        disabled={disabled}
      >
        {children}
      </button>
      {showLastUsedTag && <LastUsedTag />}
    </div>
  );
}

const buttonActionText = (inProgress: boolean, isSignUp: boolean) => {
  let label = 'Sign in';

  if (isSignUp) {
    label = 'Continue';
  }

  if (inProgress && !isSignUp) {
    label = 'Signing in';
  }

  if (inProgress && isSignUp) {
    label = 'Continuing';
  }

  return label;
};

export function GoogleLoginButton({
  onClick,
  inProgress,
  isSignUp,
  disabled,
  showLastUsedTag,
}: CustomButtonProps) {
  return (
    <LoginButton
      onClick={onClick}
      disabled={disabled}
      showLastUsedTag={showLastUsedTag}
      className={styles.googleBtn}
    >
      <IntegrationIcons.google width={18} height={18} customColor="#fff" />
      {buttonActionText(inProgress, isSignUp)}{' '}
      {inProgress ? <LoaderIcon customColor="#fff" /> : 'with Google'}
    </LoginButton>
  );
}

export function AppleLoginButton({
  onClick,
  inProgress,
  isSignUp,
  disabled,
  showLastUsedTag,
}: CustomButtonProps) {
  return (
    <LoginButton
      onClick={onClick}
      disabled={disabled}
      className={styles.appleBtn}
      showLastUsedTag={showLastUsedTag}
    >
      <IntegrationIcons.apple width={18} height={21} />
      {buttonActionText(inProgress, isSignUp)} {inProgress ? <LoaderIcon /> : 'with Apple'}
    </LoginButton>
  );
}

export function EmailLoginButton({
  onClick,
  inProgress,
  isSignUp,
  disabled,
  className,
}: CustomButtonProps) {
  const btn = (
    <LoginButton
      className={cn(styles.emailBtn, className)}
      onClick={onClick}
      disabled={disabled || inProgress}
    >
      {buttonActionText(inProgress, isSignUp)}{' '}
      {inProgress ? <LoaderIcon customColor="#fff" /> : 'with email'}
    </LoginButton>
  );

  return (
    <StateBasedFadeTransition
      state={String(isSignUp)}
      elementByStateMap={{
        true: <span>{btn}</span>,
        false: <span>{btn}</span>,
      }}
      duration="slow"
    />
  );
}
